import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  TrendingUp,
  TrendingDown,
  Users,
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Star,
  FileText,
  BarChart3,
  Activity,
  MessageSquare,
  Eye,
  ArrowLeft,
} from 'lucide-react';
import {
  useDayInReviewByDate,
  useGenerateDayInReview,
  useCompleteDayInReview,
  useChangesSummary,
  useForecastSummary,
  useStaffingSummary,
  useAnomalies,
} from '../../hooks/useDayInReview';

type TabType = 'summary' | 'changes' | 'forecast' | 'staffing' | 'leader';

// Mock data for demonstration
// NOTE: This mock data represents what the system can actually collect automatically:
// - Schedule changes from audit logs
// - Forecast vs actual metrics from data pipeline
// - Staffing events from TimePunch and TimeOffRequest tables
// - Leader decisions are MANUALLY logged by supervisors
// - Leader ratings/notes are MANUALLY entered during review
// - Narrative summary is system-generated from available data (no external context like marketing campaigns)
const generateMockData = (dateStr: string) => {
  const baseDate = new Date(dateStr);

  const mockReview = {
    id: 'mock-review-1',
    date: dateStr,
    organizationId: 'org-1',
    leaderOfDayId: 'user-sarah-johnson', // Assigned from schedule or manually set
    leaderRating: null, // Manual input - not yet rated
    leaderNotes: null, // Manual input - to be filled during review
    overallScore: 78.5, // Calculated from metrics: SL achieved, forecast accuracy, staffing adherence
    // System-generated narrative based only on available data (no external context)
    narrativeSummary: `${baseDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} experienced higher than expected call volume (+12.3% vs forecast).

Key Metrics:
- Morning volume was 28% above forecast between 9 AM - 11 AM
- 2 unplanned absences reduced scheduled staffing during peak hours
- 3 hours of overtime were approved across 2 agents
- 3 break times were rescheduled

Service Level Performance:
- Dipped to 72% during peak (10:00-10:30), below 85% target
- Recovered to target by 12:00 PM
- Daily average: 81.2%

Schedule Adjustments Made: 5 changes logged
Staffing Events: 6 events recorded`,
    status: 'IN_PROGRESS' as const,
    // Schedule changes - pulled from audit logs and schedule adjustment records
    scheduleChanges: [
      {
        id: 'change-1',
        dayInReviewId: 'mock-review-1',
        changeType: 'OVERTIME_ADDED',
        timestamp: new Date(baseDate.setHours(9, 15)).toISOString(),
        description: 'Overtime approved: Mike T. +2 hours (until 8:00 PM)',
        changedById: 'user-sarah-johnson',
        impactScore: null, // Impact scores could be calculated based on SL before/after
        impactNotes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'change-2',
        dayInReviewId: 'mock-review-1',
        changeType: 'BREAK_ADJUSTED',
        timestamp: new Date(baseDate.setHours(9, 30)).toISOString(),
        description: 'Break rescheduled: 3 agents moved from 10:00 AM to 11:30 AM',
        changedById: 'user-sarah-johnson',
        impactScore: null,
        impactNotes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'change-3',
        dayInReviewId: 'mock-review-1',
        changeType: 'AGENT_REASSIGNED',
        timestamp: new Date(baseDate.setHours(10, 0)).toISOString(),
        description: 'Channel reassignment: Alex R. moved from Chat to Voice',
        changedById: 'user-sarah-johnson',
        impactScore: null,
        impactNotes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'change-4',
        dayInReviewId: 'mock-review-1',
        changeType: 'OVERTIME_ADDED',
        timestamp: new Date(baseDate.setHours(10, 30)).toISOString(),
        description: 'Overtime approved: Lisa M. +1 hour (until 5:00 PM)',
        changedById: 'user-sarah-johnson',
        impactScore: null,
        impactNotes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'change-5',
        dayInReviewId: 'mock-review-1',
        changeType: 'CHANNEL_PRIORITY_CHANGED',
        timestamp: new Date(baseDate.setHours(11, 0)).toISOString(),
        description: 'Priority changed: Voice 7→9, Chat 8→6',
        changedById: 'user-sarah-johnson',
        impactScore: null,
        impactNotes: null,
        createdAt: new Date().toISOString(),
      },
    ],
    forecastMetrics: Array.from({ length: 24 }, (_, i) => {
      const hour = 8 + Math.floor(i / 2);
      const minute = (i % 2) * 30;
      const intervalStart = new Date(baseDate);
      intervalStart.setHours(hour, minute, 0, 0);
      const intervalEnd = new Date(intervalStart);
      intervalEnd.setMinutes(intervalEnd.getMinutes() + 30);

      // Create realistic variance patterns - higher in morning
      const isMorningSurge = hour >= 9 && hour <= 11;
      const volumeMultiplier = isMorningSurge ? 1.2 + Math.random() * 0.15 : 0.95 + Math.random() * 0.1;

      const forecastVolume = 45 + Math.floor(Math.random() * 30) + (isMorningSurge ? 20 : 0);
      const actualVolume = Math.round(forecastVolume * volumeMultiplier);

      return {
        id: `metric-${i}`,
        dayInReviewId: 'mock-review-1',
        intervalStart: intervalStart.toISOString(),
        intervalEnd: intervalEnd.toISOString(),
        channel: 'Voice',
        forecastVolume,
        forecastAHT: 320,
        forecastServiceLevel: 85,
        forecastOccupancy: 78,
        forecastShrinkage: 25,
        forecastStaffing: 12,
        actualVolume,
        actualAHT: 310 + Math.floor(Math.random() * 30),
        actualServiceLevel: isMorningSurge ? 72 + Math.random() * 15 : 82 + Math.random() * 8,
        actualOccupancy: isMorningSurge ? 85 + Math.random() * 10 : 75 + Math.random() * 8,
        actualShrinkage: 22 + Math.random() * 8,
        actualStaffing: isMorningSurge ? 10 : 12,
        volumeVariance: ((actualVolume - forecastVolume) / forecastVolume) * 100,
        createdAt: new Date().toISOString(),
      };
    }),
    // Staffing events - pulled from TimePunch and TimeOffRequest tables
    // System knows WHAT happened, but not WHY (reasons would need manual entry)
    staffingEvents: [
      {
        id: 'event-1',
        dayInReviewId: 'mock-review-1',
        eventType: 'CALLOUT',
        timestamp: new Date(baseDate.setHours(7, 30)).toISOString(),
        agentId: 'agent-maria',
        plannedValue: 'Scheduled 8:00 AM - 4:00 PM',
        actualValue: 'Callout submitted at 7:30 AM', // System only knows it was a callout
        duration: 480,
        staffingImpact: -1,
        notes: null, // Reason not automatically known
        createdAt: new Date().toISOString(),
      },
      {
        id: 'event-2',
        dayInReviewId: 'mock-review-1',
        eventType: 'LATE_ARRIVAL',
        timestamp: new Date(baseDate.setHours(8, 22)).toISOString(),
        agentId: 'agent-james',
        plannedValue: 'Scheduled start: 8:00 AM',
        actualValue: 'First punch: 8:22 AM (+22 min late)',
        duration: 22,
        staffingImpact: 0,
        notes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'event-3',
        dayInReviewId: 'mock-review-1',
        eventType: 'NO_SHOW',
        timestamp: new Date(baseDate.setHours(9, 0)).toISOString(),
        agentId: 'agent-james-k',
        plannedValue: 'Scheduled 9:00 AM - 5:00 PM',
        actualValue: 'No punch recorded',
        duration: 480,
        staffingImpact: -1,
        notes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'event-4',
        dayInReviewId: 'mock-review-1',
        eventType: 'OVERTIME_APPROVED',
        timestamp: new Date(baseDate.setHours(9, 15)).toISOString(),
        agentId: 'agent-mike',
        plannedValue: 'Original shift: 10:00 AM - 6:00 PM',
        actualValue: 'Extended to 8:00 PM (+2 hours)',
        duration: 120,
        staffingImpact: 1,
        notes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'event-5',
        dayInReviewId: 'mock-review-1',
        eventType: 'OVERTIME_APPROVED',
        timestamp: new Date(baseDate.setHours(10, 30)).toISOString(),
        agentId: 'agent-lisa',
        plannedValue: 'Original shift: 8:00 AM - 4:00 PM',
        actualValue: 'Extended to 5:00 PM (+1 hour)',
        duration: 60,
        staffingImpact: 1,
        notes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'event-6',
        dayInReviewId: 'mock-review-1',
        eventType: 'BREAK_MOVED',
        timestamp: new Date(baseDate.setHours(9, 30)).toISOString(),
        agentId: 'agent-group',
        plannedValue: 'Original break: 10:00 AM (3 agents)',
        actualValue: 'Rescheduled to 11:30 AM',
        duration: 0,
        staffingImpact: 0,
        notes: null,
        createdAt: new Date().toISOString(),
      },
    ],
    channelMetrics: [
      {
        id: 'channel-1',
        dayInReviewId: 'mock-review-1',
        channel: 'Voice',
        priorityScore: 9,
        priorityChanges: 1,
        targetServiceLevel: 85,
        achievedServiceLevel: 81.2,
        wasDeprioritized: false,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'channel-2',
        dayInReviewId: 'mock-review-1',
        channel: 'Chat',
        priorityScore: 6,
        priorityChanges: 1,
        targetServiceLevel: 80,
        achievedServiceLevel: 74.5,
        wasDeprioritized: true,
        deprioritizedFor: 'Voice',
        tradeoffJustification: 'Voice volume spike required additional resources',
        createdAt: new Date().toISOString(),
      },
    ],
    // Leader decisions - these are MANUALLY LOGGED by the supervisor during/after shift
    // The system provides a form for leaders to document their decisions
    // Effectiveness evaluation is added later during review
    leaderDecisions: [
      {
        id: 'decision-1',
        dayInReviewId: 'mock-review-1',
        timestamp: new Date(baseDate.setHours(9, 15)).toISOString(),
        decisionType: 'OVERTIME_APPROVAL',
        description: 'Approved overtime for two agents', // Logged by leader
        situationBefore: null, // Optional context field
        actionTaken: null,
        outcome: null,
        wasEffective: null, // To be evaluated during review
        evaluationNotes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'decision-2',
        dayInReviewId: 'mock-review-1',
        timestamp: new Date(baseDate.setHours(9, 30)).toISOString(),
        decisionType: 'BREAK_COORDINATION',
        description: 'Rescheduled 3 breaks to maintain coverage',
        situationBefore: null,
        actionTaken: null,
        outcome: null,
        wasEffective: null,
        evaluationNotes: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'decision-3',
        dayInReviewId: 'mock-review-1',
        timestamp: new Date(baseDate.setHours(10, 0)).toISOString(),
        decisionType: 'CHANNEL_PRIORITIZATION',
        description: 'Moved agent from Chat to Voice',
        situationBefore: null,
        actionTaken: null,
        outcome: null,
        wasEffective: null,
        evaluationNotes: null,
        createdAt: new Date().toISOString(),
      },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const mockChangesSummary = {
    totalChanges: 5,
    byType: {
      OVERTIME_ADDED: 2,
      BREAK_ADJUSTED: 1,
      AGENT_REASSIGNED: 1,
      CHANNEL_PRIORITY_CHANGED: 1,
    },
    positiveImpact: 4,
    negativeImpact: 0,
    neutralImpact: 1,
  };

  const mockForecastSummary = {
    totalIntervals: 24,
    volume: {
      forecast: 1440,
      actual: 1617,
      variance: 12.3,
      intervalsWithData: 24,
    },
    serviceLevel: {
      averageVariance: -3.8,
      intervalsWithData: 24,
    },
    occupancy: {
      averageVariance: 5.2,
      intervalsWithData: 24,
    },
    staffing: {
      totalVariance: -8,
      averageVariance: -0.33,
      intervalsWithData: 24,
    },
  };

  const mockStaffingSummary = {
    totalEvents: 6,
    byType: {
      CALLOUT: 1,
      NO_SHOW: 1,
      LATE_ARRIVAL: 1,
      OVERTIME_APPROVED: 2,
      BREAK_MOVED: 1,
    },
    totalStaffingImpact: 0,
    callouts: 2,
    lateArrivals: 1,
    earlyDepartures: 0,
    overtimeApproved: 2,
    breakAdjustments: 1,
    ptoCount: 0,
  };

  // Anomalies - system-detected deviations from expected values
  // These are automatically flagged based on threshold rules
  const mockAnomalies = [
    {
      intervalStart: new Date(baseDate.setHours(9, 30)).toISOString(),
      type: 'volume_spike',
      description: 'Volume 28% above forecast (threshold: 15%)',
      severity: 'high' as const,
    },
    {
      intervalStart: new Date(baseDate.setHours(10, 0)).toISOString(),
      type: 'service_level_drop',
      description: 'Service level at 72% (target: 85%, threshold: 80%)',
      severity: 'high' as const,
    },
    {
      intervalStart: new Date(baseDate.setHours(10, 30)).toISOString(),
      type: 'occupancy_high',
      description: 'Occupancy at 92% (threshold: 85%)',
      severity: 'medium' as const,
    },
    {
      intervalStart: new Date(baseDate.setHours(14, 0)).toISOString(),
      type: 'aht_variance',
      description: 'AHT 15% below forecast (threshold: 10%)',
      severity: 'low' as const,
    },
  ];

  // Score breakdown - transparent calculation based on measurable metrics
  // AHT: target 360s (6:00), actual 402s (6:42) = +42s over goal = 89.6% score
  const mockScoreBreakdown = {
    overallScore: 94.5, // 38.2 + 29.4 + 26.9 = 94.5
    components: [
      {
        metric: 'Service Level',
        weight: 40,
        target: 85,
        actual: 81.2,
        score: 95.5, // (81.2/85) * 100 = 95.5
        weightedScore: 38.2, // 95.5 * 0.4
      },
      {
        metric: 'Occupancy',
        weight: 30,
        target: 80,
        actual: 78.5,
        score: 98.1, // within target range (65-85% ideal)
        weightedScore: 29.4,
      },
      {
        metric: 'AHT',
        weight: 30,
        target: 360, // Target AHT in seconds (6:00)
        actual: 402, // Actual AHT in seconds (6:42) - +42s over goal
        score: 89.6, // (360/402) * 100 = 89.6% (loses points for being over)
        weightedScore: 26.9,
      },
    ],
  };

  return {
    review: mockReview,
    changesSummary: mockChangesSummary,
    forecastSummary: mockForecastSummary,
    staffingSummary: mockStaffingSummary,
    anomalies: mockAnomalies,
    scoreBreakdown: mockScoreBreakdown,
  };
};

export default function DayInReviewPage() {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const [showMockData, setShowMockData] = useState(true);

  const { data: apiReview, isLoading: reviewLoading } = useDayInReviewByDate(selectedDate);
  const generateReview = useGenerateDayInReview();
  const completeReview = useCompleteDayInReview();

  // Generate mock data for the selected date
  const mockData = useMemo(() => generateMockData(selectedDate), [selectedDate]);

  // Use API data if available, otherwise use mock data when showMockData is true
  const review = apiReview || (showMockData ? mockData.review : null);
  const isUsingMockData = !apiReview && showMockData;

  // Summary data - use API data or mock data
  const { data: apiChangesSummary } = useChangesSummary(apiReview?.id || '');
  const { data: apiForecastSummary } = useForecastSummary(apiReview?.id || '');
  const { data: apiStaffingSummary } = useStaffingSummary(apiReview?.id || '');
  const { data: apiAnomalies } = useAnomalies(apiReview?.id || '');

  const changesSummary = apiChangesSummary || (isUsingMockData ? mockData.changesSummary : undefined);
  const forecastSummary = apiForecastSummary || (isUsingMockData ? mockData.forecastSummary : undefined);
  const staffingSummary = apiStaffingSummary || (isUsingMockData ? mockData.staffingSummary : undefined);
  const anomalies = apiAnomalies || (isUsingMockData ? mockData.anomalies : []);
  const scoreBreakdown = isUsingMockData ? mockData.scoreBreakdown : undefined;

  const handlePreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date <= today) {
      setSelectedDate(date.toISOString().split('T')[0]);
    }
  };

  const handleGenerateReview = async () => {
    try {
      await generateReview.mutateAsync({ date: selectedDate });
    } catch (error) {
      console.error('Failed to generate review:', error);
    }
  };

  const handleCompleteReview = async () => {
    if (review?.id) {
      try {
        await completeReview.mutateAsync(review.id);
      } catch (error) {
        console.error('Failed to complete review:', error);
      }
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-blue-600 bg-blue-100';
    if (score >= 55) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'IN_PROGRESS':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            In Progress
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Draft
          </span>
        );
    }
  };

  const tabs = [
    { id: 'summary' as TabType, label: 'Summary', icon: FileText },
    { id: 'changes' as TabType, label: 'Schedule Changes', icon: Activity },
    { id: 'forecast' as TabType, label: 'Forecast vs Actual', icon: BarChart3 },
    { id: 'staffing' as TabType, label: 'Staffing Events', icon: Users },
    { id: 'leader' as TabType, label: 'Leader of Day', icon: Star },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          {/* Back to Reporting Link */}
          <Link
            to="/reporting"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Reporting
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Day in Review</h1>
              <p className="text-sm text-gray-500 mt-1">
                Evaluate operational performance and decisions
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Preview Mode Toggle */}
              {!apiReview && (
                <button
                  onClick={() => setShowMockData(!showMockData)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border transition-colors ${
                    showMockData
                      ? 'bg-purple-50 border-purple-300 text-purple-700'
                      : 'bg-gray-50 border-gray-300 text-gray-600'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {showMockData ? 'Preview Mode' : 'Show Preview'}
                  </span>
                </button>
              )}

              {/* Preview Mode Badge */}
              {isUsingMockData && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <Eye className="w-3 h-3 mr-1" />
                  Sample Data
                </span>
              )}

              {review && !isUsingMockData && (
                <>
                  {getStatusBadge(review.status)}
                  {review.status !== 'COMPLETED' && (
                    <button
                      onClick={handleCompleteReview}
                      disabled={completeReview.isPending}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {completeReview.isPending ? 'Completing...' : 'Complete Review'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Date Navigator */}
          <div className="flex items-center justify-center mt-6 space-x-4">
            <button
              onClick={handlePreviousDay}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <button
              onClick={handleNextDay}
              disabled={selectedDate >= new Date().toISOString().split('T')[0]}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <p className="text-center text-lg font-medium text-gray-700 mt-2">
            {formatDate(selectedDate)}
          </p>
        </div>
      </div>

      {/* Loading state */}
      {reviewLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      )}

      {/* No review state */}
      {!reviewLoading && !review && !showMockData && (
        <div className="flex items-center justify-center py-20">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Review Available</h2>
            <p className="text-gray-600 mb-6">
              There's no review for {formatDate(selectedDate)} yet. Generate one to analyze
              the day's performance, or view sample data to see how the feature works.
            </p>
            <div className="flex flex-col space-y-3">
              <button
                onClick={handleGenerateReview}
                disabled={generateReview.isPending}
                className="inline-flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {generateReview.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    <span>Generate Review</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setShowMockData(true)}
                className="inline-flex items-center justify-center space-x-2 px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
              >
                <Eye className="h-4 w-4" />
                <span>View Sample Data</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Content */}
      {!reviewLoading && review && (
        <>
          {/* Score Cards */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Overall Score */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Overall Score</span>
                  <Star className="w-5 h-5 text-yellow-500" />
                </div>
                <div className="mt-2">
                  <span
                    className={`text-3xl font-bold px-2 py-1 rounded ${getScoreColor(review.overallScore || 0)}`}
                  >
                    {review.overallScore?.toFixed(1) || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Volume Variance */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Volume Variance</span>
                  {forecastSummary && forecastSummary.volume.variance > 0 ? (
                    <TrendingUp className="w-5 h-5 text-red-500" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-green-500" />
                  )}
                </div>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {forecastSummary
                      ? `${forecastSummary.volume.variance > 0 ? '+' : ''}${forecastSummary.volume.variance.toFixed(1)}%`
                      : 'N/A'}
                  </span>
                </div>
              </div>

              {/* Schedule Changes */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Schedule Changes</span>
                  <Activity className="w-5 h-5 text-blue-500" />
                </div>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {changesSummary?.totalChanges || 0}
                  </span>
                </div>
              </div>

              {/* Staffing Events */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Staffing Events</span>
                  <Users className="w-5 h-5 text-purple-500" />
                </div>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {staffingSummary?.totalEvents || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                        ${
                          activeTab === tab.id
                            ? 'border-purple-500 text-purple-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'summary' && (
                  <SummaryTab
                    anomalies={anomalies || []}
                    changesSummary={changesSummary}
                    staffingSummary={staffingSummary}
                    scoreBreakdown={scoreBreakdown}
                  />
                )}
                {activeTab === 'changes' && (
                  <ScheduleChangesTab
                    changes={review.scheduleChanges}
                    summary={changesSummary}
                  />
                )}
                {activeTab === 'forecast' && (
                  <ForecastTab metrics={review.forecastMetrics} summary={forecastSummary} />
                )}
                {activeTab === 'staffing' && (
                  <StaffingTab events={review.staffingEvents} summary={staffingSummary} />
                )}
                {activeTab === 'leader' && (
                  <LeaderTab review={review} />
                )}
              </div>
            </div>
          </div>

          {/* Narrative Summary */}
          {review.narrativeSummary && (
            <div className="px-6 py-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-purple-500" />
                  <h3 className="text-lg font-semibold text-gray-900">Narrative Summary</h3>
                </div>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {review.narrativeSummary}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Sub-components for tabs
function SummaryTab({
  anomalies,
  changesSummary,
  staffingSummary,
  scoreBreakdown,
}: {
  anomalies: any[];
  changesSummary: any;
  staffingSummary: any;
  scoreBreakdown?: any;
}) {
  const highSeverityAnomalies = anomalies.filter((a) => a.severity === 'high');

  return (
    <div className="space-y-6">
      {/* Score Breakdown */}
      {scoreBreakdown && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-purple-900 mb-3">Overall Score Calculation</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {scoreBreakdown.components.map((component: any, idx: number) => {
              // Format AHT differently (time-based)
              const isAHT = component.metric === 'AHT';
              const formatTime = (secs: number) => {
                const mins = Math.floor(secs / 60);
                const s = Math.round(secs % 60);
                return `${mins}:${s.toString().padStart(2, '0')}`;
              };

              let displayValue: string;
              let displayTarget: string;
              let varianceText: string | null = null;

              if (isAHT) {
                displayValue = formatTime(component.actual);
                displayTarget = formatTime(component.target);
                const diff = component.actual - component.target;
                if (diff > 0) {
                  const pctOver = ((diff / component.target) * 100).toFixed(0);
                  varianceText = `+${formatTime(diff)} (+${pctOver}% over)`;
                } else {
                  varianceText = 'On target';
                }
              } else {
                displayValue = `${component.actual.toFixed(1)}%`;
                displayTarget = `${component.target}%`;
              }

              return (
                <div key={idx} className="bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500">{component.metric}</span>
                    <span className="text-xs text-purple-600">{component.weight}% weight</span>
                  </div>
                  <div className="flex items-baseline space-x-2">
                    <span className="text-lg font-bold text-gray-900">{displayValue}</span>
                    <span className="text-xs text-gray-500">/ {displayTarget} target</span>
                  </div>
                  {isAHT && varianceText && (
                    <div className={`mt-1 text-xs font-medium ${component.actual > component.target ? 'text-red-600' : 'text-green-600'}`}>
                      {varianceText}
                    </div>
                  )}
                  <div className="mt-1 text-xs text-gray-600">
                    Contributes {component.weightedScore.toFixed(1)} pts
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-purple-200 flex items-center justify-between">
            <span className="text-sm text-purple-700">
              Formula: (SL Score × 40%) + (Occupancy Score × 30%) + (AHT × 30%)
            </span>
            <span className="text-lg font-bold text-purple-900">
              = {scoreBreakdown.overallScore.toFixed(1)}
            </span>
          </div>
        </div>
      )}

      {/* Anomalies Alert */}
      {highSeverityAnomalies.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h4 className="font-semibold text-red-800">High Priority Issues</h4>
          </div>
          <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
            {highSeverityAnomalies.map((anomaly, idx) => (
              <li key={idx}>{anomaly.description}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Staffing Impact</h4>
          <div className="text-2xl font-bold text-gray-900">
            {staffingSummary
              ? `${staffingSummary.totalStaffingImpact > 0 ? '+' : ''}${staffingSummary.totalStaffingImpact}`
              : 'N/A'}
          </div>
          <p className="text-sm text-gray-500">
            {staffingSummary?.callouts || 0} callouts, {staffingSummary?.overtimeApproved || 0}{' '}
            overtime approvals
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Schedule Changes</h4>
          <div className="text-2xl font-bold text-gray-900">
            {changesSummary?.totalChanges || 0}
          </div>
          <p className="text-sm text-gray-500">
            changes logged
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Anomalies Detected</h4>
          <div className="text-2xl font-bold text-gray-900">{anomalies.length}</div>
          <p className="text-sm text-gray-500">
            {highSeverityAnomalies.length} high severity
          </p>
        </div>
      </div>

      {/* All Anomalies */}
      {anomalies.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">All Detected Anomalies</h4>
          <div className="space-y-2">
            {anomalies.map((anomaly, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  anomaly.severity === 'high'
                    ? 'bg-red-50'
                    : anomaly.severity === 'medium'
                      ? 'bg-yellow-50'
                      : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      anomaly.severity === 'high'
                        ? 'bg-red-500'
                        : anomaly.severity === 'medium'
                          ? 'bg-yellow-500'
                          : 'bg-gray-400'
                    }`}
                  />
                  <span className="text-sm text-gray-700">{anomaly.description}</span>
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(anomaly.intervalStart).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleChangesTab({ changes, summary }: { changes: any[]; summary: any }) {
  return (
    <div className="space-y-6">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(summary.byType).map(([type, count]) => (
            <div key={type} className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-gray-900">{count as number}</div>
              <div className="text-xs text-gray-500">
                {type.replace(/_/g, ' ').toLowerCase()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Change Timeline</h4>
        {changes.length === 0 ? (
          <p className="text-gray-500 text-sm">No schedule changes recorded for this day.</p>
        ) : (
          <div className="space-y-3">
            {changes.map((change) => (
              <div
                key={change.id}
                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-shrink-0 mt-1">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      change.impactScore && change.impactScore > 0
                        ? 'bg-green-500'
                        : change.impactScore && change.impactScore < 0
                          ? 'bg-red-500'
                          : 'bg-gray-400'
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{change.description}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(change.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {' - '}
                    {change.changeType.replace(/_/g, ' ').toLowerCase()}
                  </p>
                  {change.impactNotes && (
                    <p className="text-xs text-gray-600 mt-1">{change.impactNotes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ForecastTab({ metrics, summary }: { metrics: any[]; summary: any }) {
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Total Volume</div>
            <div className="text-xl font-bold text-gray-900">
              {Math.round(summary.volume.actual)} / {Math.round(summary.volume.forecast)}
            </div>
            <div
              className={`text-sm ${summary.volume.variance > 0 ? 'text-red-600' : 'text-green-600'}`}
            >
              {summary.volume.variance > 0 ? '+' : ''}
              {summary.volume.variance.toFixed(1)}%
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Avg Service Level Var</div>
            <div className="text-xl font-bold text-gray-900">
              {summary.serviceLevel.averageVariance > 0 ? '+' : ''}
              {summary.serviceLevel.averageVariance.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500">
              {summary.serviceLevel.intervalsWithData} intervals
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Avg Occupancy Var</div>
            <div className="text-xl font-bold text-gray-900">
              {summary.occupancy.averageVariance > 0 ? '+' : ''}
              {summary.occupancy.averageVariance.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500">
              {summary.occupancy.intervalsWithData} intervals
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Staffing Variance</div>
            <div className="text-xl font-bold text-gray-900">
              {summary.staffing.totalVariance > 0 ? '+' : ''}
              {summary.staffing.totalVariance}
            </div>
            <div className="text-sm text-gray-500">agents net</div>
          </div>
        </div>
      )}

      {/* Metrics Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Interval Details</h4>
        {metrics.length === 0 ? (
          <p className="text-gray-500 text-sm">No forecast metrics available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Time
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Volume (F/A)
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    SL (F/A)
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Occ (F/A)
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Staff (F/A)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {metrics.slice(0, 20).map((metric) => (
                  <tr key={metric.id}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {new Date(metric.intervalStart).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-3 py-2 text-sm text-right">
                      <span className="text-gray-500">{Math.round(metric.forecastVolume)}</span>
                      <span className="text-gray-400"> / </span>
                      <span className="text-gray-900">
                        {metric.actualVolume !== null ? Math.round(metric.actualVolume) : '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-right">
                      <span className="text-gray-500">{metric.forecastServiceLevel}%</span>
                      <span className="text-gray-400"> / </span>
                      <span className="text-gray-900">
                        {metric.actualServiceLevel !== null
                          ? `${metric.actualServiceLevel.toFixed(1)}%`
                          : '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-right">
                      <span className="text-gray-500">{metric.forecastOccupancy}%</span>
                      <span className="text-gray-400"> / </span>
                      <span className="text-gray-900">
                        {metric.actualOccupancy !== null
                          ? `${metric.actualOccupancy.toFixed(1)}%`
                          : '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-right">
                      <span className="text-gray-500">{metric.forecastStaffing}</span>
                      <span className="text-gray-400"> / </span>
                      <span className="text-gray-900">
                        {metric.actualStaffing !== null ? metric.actualStaffing : '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {metrics.length > 20 && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Showing first 20 of {metrics.length} intervals
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StaffingTab({ events, summary }: { events: any[]; summary: any }) {
  return (
    <div className="space-y-6">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-red-700">{summary.callouts}</div>
            <div className="text-xs text-red-600">Callouts/No Shows</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-yellow-700">{summary.lateArrivals}</div>
            <div className="text-xs text-yellow-600">Late Arrivals</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-orange-700">{summary.earlyDepartures}</div>
            <div className="text-xs text-orange-600">Early Departures</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-green-700">{summary.overtimeApproved}</div>
            <div className="text-xs text-green-600">Overtime Approved</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-blue-700">{summary.ptoCount}</div>
            <div className="text-xs text-blue-600">PTO</div>
          </div>
        </div>
      )}

      {/* Events List */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Event Timeline</h4>
        {events.length === 0 ? (
          <p className="text-gray-500 text-sm">No staffing events recorded for this day.</p>
        ) : (
          <div className="space-y-2">
            {events.map((event) => (
              <div
                key={event.id}
                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-shrink-0 mt-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      event.eventType.includes('CALLOUT') || event.eventType.includes('NO_SHOW')
                        ? 'bg-red-100 text-red-800'
                        : event.eventType.includes('LATE') || event.eventType.includes('EARLY')
                          ? 'bg-yellow-100 text-yellow-800'
                          : event.eventType.includes('OVERTIME')
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {event.eventType.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{event.actualValue || event.eventType}</p>
                  {event.plannedValue && (
                    <p className="text-xs text-gray-500">Planned: {event.plannedValue}</p>
                  )}
                  <p className="text-xs text-gray-400">
                    {new Date(event.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {event.duration && ` - ${event.duration} min affected`}
                  </p>
                </div>
                {event.staffingImpact !== null && event.staffingImpact !== 0 && (
                  <span
                    className={`text-sm font-medium ${
                      event.staffingImpact > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {event.staffingImpact > 0 ? '+' : ''}
                    {event.staffingImpact}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LeaderTab({ review }: { review: any }) {
  const decisions = review.leaderDecisions || [];

  return (
    <div className="space-y-6">
      {/* Leader Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Leader of the Day</h4>
        {review.leaderOfDayId ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-900">Leader ID: {review.leaderOfDayId}</p>
              {review.leaderRating && (
                <div className="flex items-center space-x-1 mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= review.leaderRating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="text-sm text-gray-600 ml-2">
                    ({review.leaderRating}/5)
                  </span>
                </div>
              )}
              {review.leaderNotes && (
                <p className="text-sm text-gray-600 mt-2">{review.leaderNotes}</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No leader assigned for this day.</p>
        )}
      </div>

      {/* Decisions */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Leader Decisions ({decisions.length})
        </h4>
        {decisions.length === 0 ? (
          <p className="text-gray-500 text-sm">No decisions recorded for this day.</p>
        ) : (
          <div className="space-y-3">
            {decisions.map((decision: any) => (
              <div key={decision.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800`}
                    >
                      {decision.decisionType.replace(/_/g, ' ')}
                    </span>
                    <p className="text-sm font-medium text-gray-900 mt-2">
                      {decision.description}
                    </p>
                  </div>
                  {decision.wasEffective !== null && (
                    <span
                      className={`text-sm font-medium ${
                        decision.wasEffective ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {decision.wasEffective ? 'Effective' : 'Ineffective'}
                    </span>
                  )}
                </div>

                {decision.situationBefore && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 uppercase">Situation</p>
                    <p className="text-sm text-gray-700">{decision.situationBefore}</p>
                  </div>
                )}

                {decision.actionTaken && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 uppercase">Action Taken</p>
                    <p className="text-sm text-gray-700">{decision.actionTaken}</p>
                  </div>
                )}

                {decision.outcome && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 uppercase">Outcome</p>
                    <p className="text-sm text-gray-700">{decision.outcome}</p>
                  </div>
                )}

                <p className="text-xs text-gray-400 mt-2">
                  {new Date(decision.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
